import type { GQL_ID } from '@@partner:types:graphql'
import { type CrudFormProps } from '@@partner:types:app'

import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  makeTextMaterialCategoryFormState,
  mapApiDataToState,
  mapStateToApiVariable,
  TextMaterialCategoryFormState
} from './TextMaterialCategoryFormState'

import { Col, DatePicker, Form, Grid, Input, InputGroup, Nav, Panel, SelectPicker, Tag } from 'rsuite'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import { useFetchSingleTextMaterialCategory } from '@@partner:graphql/query/textMaterial/categories/singleTextMaterialCategory.gql'
import { getSearchableTextMaterialStatus } from '@@partner:helpers/textMaterial'
import { useMutateUpdateTextMaterialCategory } from '@@partner:graphql/mutation/textMaterial/updateTextMaterialCategory.gql'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'
import ColorPickerField from '@components/common/commonFormInput/ColorPickerField'

export interface CategoryFormProps extends CrudFormProps {
  categoryId: GQL_ID
}

const AVAILABLE_TEXT_CATEGORY_STATUSES = getSearchableTextMaterialStatus()


export default function TextMaterialCategoryForm ({
  categoryId,
  onSaved
}: CategoryFormProps) {

  const { formState, setFormState, setFormRef } =
    useValidatableForm<TextMaterialCategoryFormState>(makeTextMaterialCategoryFormState)
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>('ru')
  
  const {
    data,
    loading: loadingTextMaterialCategory,
    error: errorLoadingTextMaterialCategory
  } = useFetchSingleTextMaterialCategory({
    id: categoryId
  })

  const [ saveTextMaterialCategory, { loading: loadingSaveTextCategory, error: errorSavingTextCategory } ] =
    useMutateUpdateTextMaterialCategory()

  useEffect(() => {
    if (!loadingTextMaterialCategory && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.singleTextMaterialCategory))
      )
    }
  }, [ data, loadingTextMaterialCategory, setFormState ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )
  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveTextMaterialCategory({
          variables: {
            textMaterialCategoryInfo: mapStateToApiVariable(formState),
            upsert: categoryId ? false : true
          }
        })
          .then(({ data }) => {
            if (data?.updateTextMaterialCategory) {
              notifyOk('Категория сохранена')
              setFormState((state) =>
                mergeLeftOnly([ 'id' ], state, mapApiDataToState(data.updateTextMaterialCategory))
              )
  
              onSaved?.(data?.updateTextMaterialCategory.id)
            }
            
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveTextMaterialCategory, formState, setFormState, onSaved, categoryId ]
  )

  if (errorLoadingTextMaterialCategory) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить категорию #${categoryId} для редактирования`}
        error={errorLoadingTextMaterialCategory}
      />
    )
  }

  if (loadingTextMaterialCategory) {
    return <AppLoader content={'Загрузка категории' } />
  }

  if (errorLoadingTextMaterialCategory) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить данные категории #${categoryId} для редактирования`}
        error={errorLoadingTextMaterialCategory}
      />
    )
  }
  
  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      layout='vertical'
      fluid
    >
      
      <Grid fluid>
        <Col xs={24} md={16}>
          <Panel
            shaded
            className='my-5'
          >
            <Form.Group>
              <Form.ControlLabel>Название (техническое)</Form.ControlLabel>
              <Form.Control 
                name="name" 
                placeholder="Name" 
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Имя в URL</Form.ControlLabel>
              <Form.Control 
                name="slug" 
                placeholder="Имя в URL" 
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Статус</Form.ControlLabel>
              <Form.Control 
                name='status' 
                value={formState.status}
                accepter={SelectPicker} 
                data={AVAILABLE_TEXT_CATEGORY_STATUSES} 
              />
            </Form.Group>
            <Form.ControlLabel>Период действия</Form.ControlLabel>

            <InputGroup>
              <Form.Control
                accepter={DatePicker}
                block
                oneTap
                isoWeek
                appearance="subtle"
                name="activeFrom"
                value={formState.activeFrom}
                format="yyyy-MM-dd HH:mm:ss"
                placeholder="дата начала действия"
              />

              <InputGroup.Addon> до </InputGroup.Addon>

              <Form.Control
                accepter={DatePicker}
                block
                isoWeek
                oneTap
                appearance="subtle"
                name="activeUntil"
                value={formState.activeUntil}
                format="yyyy-MM-dd HH:mm:ss"
                placeholder="дата окончания действия"
              />
            </InputGroup>

            <Form.Group>
              <Form.ControlLabel>Вес</Form.ControlLabel>
              <Form.Control name="priority_weight" type="number" />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Цвет</Form.ControlLabel>
              <ColorPickerField
                label='Цвет категории'
                value={formState.color ?? ''}
                onChange={(color) => {
                  setFormState((state) => mergeLeft(state, { color }))
                }}
              />
            </Form.Group>
          </Panel>
        </Col>
        
        <Col xs={24} md={16}>
          <SelectLocale 
            languages={formState.languages} 
            selectedLanguage={selectedLanguage}
            onSelect={(language) => setSelectedLanguage(language)}
            onAdd={(newLanguage) => {
              if (newLanguage && newLanguage.length > 0) {
                const newLanguages = [ ...formState.languages, newLanguage ]
                setFormState((state) => mergeLeft(state, { languages: newLanguages }))
              }
            }}
          />
          
          <Panel 
            shaded
            className='my-5'
          >
            <LocalizedForm
              name='title'
              label='Название'
              selectedLanguage={selectedLanguage}
              value={formState.i18n?.find((translation) => translation.languageCode === selectedLanguage)?.title || ''}
              onChange={(value: string) => {
                const newTranslations = [ ...formState.i18n ]
                const titleIndex = newTranslations.findIndex(translation => translation.languageCode === selectedLanguage)
                if (titleIndex > -1) {
                  newTranslations[titleIndex].title = value
                }
                else {
                  newTranslations.push({ languageCode: selectedLanguage, title: value })
                }
                setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
              }}
            />
          </Panel>
          
        </Col>
      </Grid>

      <SaveButton
        loading={loadingTextMaterialCategory || loadingSaveTextCategory}
        disabled={loadingTextMaterialCategory || loadingSaveTextCategory}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
